import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import { LessonMode } from '../../../Constants';
import { register } from '../../../i18n';

import Guideline from '../tools/Guideline';
import HtmlComponent from '../HtmlComponent';
import ShowPreviousResponseButton from '../ShowPreviousResponseButton';
import ShowPreviousResponseConfirmDialog from '../ShowPreviousResponseConfirmDialog';
import PreviewUnavailableDialog from '../PreviewUnavailableDialog';
import useStyleEvents from '../../../hooks/useStyleEvents';

import AuthService from '../../services/AuthService';
import PlayerService from '../../services/PlayerService';
import responseService from '../../services/ResponseService';
import '../../../css/MicrositeQuestion.scss';

const t = register('GlobalQuestionLabels');

const buildMicrositeUrl = ({
  micrositeEntityId,
  activityId,
  activityResponseId,
  sourceActivityResponseId,
  sourceActivityId,
}) => {
  if (!micrositeEntityId) {
    return '';
  }

  const params = new URLSearchParams({
    resourceId: micrositeEntityId,
    authKey: AuthService.authKey,
  });

  if (activityId) {
    params.set('activityId', activityId);
  }

  if (activityResponseId) {
    params.set('activityResponseId', activityResponseId);
  }

  if (sourceActivityResponseId) {
    params.set('sourceActivityResponseId', sourceActivityResponseId);
  }

  if (sourceActivityId) {
    params.set('sourceActivityId', sourceActivityId);
  }

  return `${AuthService.ecms}/api/redirectToMicrosite?${params.toString()}`;
};

const MicrositeQuestion = observer(({ lessonElementId }) => {
  const {
    lessonManager,
    toolbarManager,
    studentActivityManager,
    responseManager,
  } = useContext(MobXProviderContext);

  const model = lessonManager.getSlideModel(lessonElementId);
  const lessonElementState = responseManager.getLessonElementState(lessonElementId);
  const iframeRef = useRef(null);
  const [pullForwardSource, setPullForwardSource] = useState(null);
  const [iframeReloadNonce, setIframeReloadNonce] = useState(0);
  const [showPreviousResponseConfirm, setShowPreviousResponseConfirm] = useState(false);
  const [showPreviewUnavailable, setShowPreviewUnavailable] = useState(false);

  useStyleEvents(lessonElementId, `${lessonElementId}-microsite-view`);

  useEffect(() => {
    setPullForwardSource(null);
    setIframeReloadNonce(0);
  }, [lessonElementId]);

  const responseDependencyValue = lessonManager.lessonOptions?.responseDependency?.[lessonElementId];
  let hasResponseDependency = false;
  let isDifferentLessonSource = false;

  if (responseDependencyValue) {
    if (typeof responseDependencyValue === 'object') {
      if (responseDependencyValue.source === 'inLesson') {
        hasResponseDependency = responseDependencyValue.questionId;
      } else if (responseDependencyValue.source === 'differentLesson') {
        isDifferentLessonSource = true;
        hasResponseDependency = responseDependencyValue.lessonId &&
          responseDependencyValue.questionForeverId &&
          responseDependencyValue.lessonId !== '' &&
          responseDependencyValue.questionForeverId !== '';
      }
    } else {
      hasResponseDependency = responseDependencyValue !== null && responseDependencyValue !== '';
    }
  }

  const isSubmitted = lessonElementState?.isSubmitted;

  const micrositeUrl = useMemo(() => buildMicrositeUrl({
    micrositeEntityId: model?.micrositeEntityId,
    activityId: studentActivityManager?.activityId,
    activityResponseId: lessonElementState?.responseId,
    sourceActivityResponseId: pullForwardSource?.sourceActivityResponseId,
    sourceActivityId: pullForwardSource?.sourceActivityId,
  }), [
    model?.micrositeEntityId,
    studentActivityManager?.activityId,
    lessonElementState?.responseId,
    pullForwardSource,
  ]);

  const applyPullForwardSource = (sourceContext) => {
    if (sourceContext?.sourceActivityResponseId) {
      setPullForwardSource(sourceContext);
      setIframeReloadNonce((nonce) => nonce + 1);
    }
  };

  const resolveAndApplyPullForward = async () => {
    const questionBehavior = lessonElementState?.questionBehavior;
    if (!questionBehavior?.getInLessonPullForwardSourceContext) {
      return;
    }

    let sourceContext = null;

    if (isDifferentLessonSource) {
      sourceContext = await questionBehavior.fetchPullForwardSourceContextFromDifferentLesson(responseDependencyValue);
    } else {
      sourceContext = questionBehavior.getInLessonPullForwardSourceContext(model, lessonElementId);
      if (!sourceContext?.sourceActivityResponseId) {
        responseService.alertEmptyPreviousResponse();
        return;
      }
    }

    applyPullForwardSource(sourceContext);
  };

  const handleShowPreviousResponse = async () => {
    if (!hasResponseDependency) {
      return;
    }

    if (isDifferentLessonSource) {
      if (!PlayerService.isActivityMode()) {
        setShowPreviewUnavailable(true);
        return;
      }
    }

    if (pullForwardSource?.sourceActivityResponseId) {
      setShowPreviousResponseConfirm(true);
      return;
    }

    await resolveAndApplyPullForward();
  };

  const handleConfirmShowPreviousResponse = async () => {
    setShowPreviousResponseConfirm(false);
    await resolveAndApplyPullForward();
  };

  const handleCancelShowPreviousResponse = () => {
    setShowPreviousResponseConfirm(false);
  };

  const handleIframeLoad = () => {
    if (model?.loadEventScript && iframeRef.current) {
      try {
        const executeScript = new Function(model.loadEventScript);
        executeScript();
      } catch (error) {
        console.error('Error executing loadEventScript:', error);
      }
    }
  };

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !micrositeUrl) {
      return undefined;
    }

    iframe.addEventListener('load', handleIframeLoad);
    if (iframe.src !== micrositeUrl) {
      iframe.src = micrositeUrl;
    }

    return () => {
      iframe.removeEventListener('load', handleIframeLoad);
    };
  }, [lessonElementId, model?.loadEventScript, micrositeUrl, iframeReloadNonce]);

  if (!lessonElementState) {
    return (<div className='microsite-question' />);
  }

  const iframeKey = [
    lessonElementId,
    lessonElementState.responseId || 'default',
    pullForwardSource?.sourceActivityResponseId || 'none',
    iframeReloadNonce,
  ].join('-');

  return (
    <div className='microsite-question' id={`${lessonElementId}-microsite-view`}>
      {toolbarManager.isGuidelineOpen && <Guideline lessonElementId={lessonElementId} />}
      <div className='test-item-question lesson-element microsite-question-container'>
        {model?.questionText && (
          <div className='microsite-question-text'>
            <HtmlComponent htmlStr={model.questionText} />
          </div>
        )}

        {hasResponseDependency && lessonManager.playerMode !== LessonMode.SCORING && lessonManager.playerMode !== LessonMode.REVIEW && (
          <div className='response-dependency-controls'>
            <ShowPreviousResponseButton
              lessonElementId={lessonElementId}
              onShowPreviousResponse={handleShowPreviousResponse}
              isDisabled={isSubmitted}
            />
          </div>
        )}

        {micrositeUrl && (
          <div className='microsite-iframe-container'>
            <iframe
              ref={iframeRef}
              key={iframeKey}
              allowFullScreen={true}
              id={`${lessonElementId}-microsite-iframe`}
              src={micrositeUrl}
              title={model.title || 'Microsite'}
              className='microsite-iframe'
            />
          </div>
        )}
      </div>

      <ShowPreviousResponseConfirmDialog
        isOpen={showPreviousResponseConfirm}
        onConfirm={handleConfirmShowPreviousResponse}
        onCancel={handleCancelShowPreviousResponse}
        message={t('showPreviousResponseWarning')}
      />

      <PreviewUnavailableDialog
        isOpen={showPreviewUnavailable}
        onClose={() => setShowPreviewUnavailable(false)}
        message='This feature is only available during live question answering sessions. It cannot be previewed in this mode.'
      />
    </div>
  );
});

export default MicrositeQuestion;
